import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Banknote, Briefcase, ArrowRight, Loader2 } from "lucide-react";
import { PublicHeader } from "../components/PublicHeader";
import { SiteFooter } from "../components/SiteFooter";
import { api } from "../lib/api";

const JobCard = ({ role }) => {
  const navigate = useNavigate();
  return (
  <article
    data-testid={`job-card-${role.id}`}
    onClick={() => navigate(`/jobs/${role.id}`)}
    className="group flex cursor-pointer flex-col rounded-2xl border border-[#e3e0d3] bg-white p-7 transition-all hover:-translate-y-1 hover:border-[#0d2f4d] hover:shadow-lg"
  >
    <span className="mb-4 inline-flex w-fit items-center gap-1.5 rounded-full bg-[#0d2f4d] px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#C1CC24]">
      <Briefcase className="h-3 w-3" /> {role.type}
    </span>
    <h2 className="font-heading text-xl font-bold text-[#0d2f4d]">
      <Link to={`/jobs/${role.id}`} data-testid={`job-title-${role.id}`} onClick={(e) => e.stopPropagation()} className="hover:text-[#7C8514] hover:underline underline-offset-2">
        {role.title}
      </Link>
    </h2>
    {role.company && <p className="mt-0.5 text-sm font-medium text-[#7C8514]" data-testid={`job-company-${role.id}`}>{role.company}</p>}
    <div className="mt-3 space-y-1.5 text-sm text-[#51637a]">
      <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#7C8514]" /> {role.city}</p>
      {role.salary_range && (
        <p className="flex items-center gap-2"><Banknote className="h-4 w-4 text-[#7C8514]" /> {role.salary_range}</p>
      )}
    </div>
    <p className="mt-4 line-clamp-3 flex-1 text-sm leading-relaxed text-[#33475b]">{role.description}</p>
    <div className="mt-6 flex items-center justify-between gap-3">
      <Link
        to={`/jobs/${role.id}`}
        onClick={(e) => e.stopPropagation()}
        data-testid={`job-view-${role.id}`}
        className="text-sm font-semibold text-[#0d2f4d] underline-offset-2 hover:underline"
      >
        View role
      </Link>
      <Link
        to={`/signup/candidate?job=${role.id}`}
        onClick={(e) => e.stopPropagation()}
        data-testid={`job-apply-${role.id}`}
        className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C1CC24] px-5 py-2.5 text-sm font-semibold text-[#0d2f4d] transition-colors hover:bg-[#aeb81f]"
      >
        Apply <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </Link>
    </div>
  </article>
  );
};

export default function Jobs() {
  const [roles, setRoles] = useState(null);

  useEffect(() => {
    api.get("/jobs").then((r) => setRoles(r.data)).catch(() => setRoles([]));
  }, []);

  return (
    <div className="min-h-screen bg-[#f5f3ee]" data-testid="jobs-page">
      <PublicHeader />
      <main className="mx-auto max-w-6xl px-6 pb-24 pt-28">
        <header className="mb-14 text-center">
          <p className="font-accent text-xl text-[#7C8514]">Live roles</p>
          <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight text-[#0d2f4d] sm:text-5xl">
            Open positions
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-[#51637a]">
            Hand-picked roles from employers who hire the Ditch way. See one that fits? Apply in a minute and
            we'll take it from there.
          </p>
        </header>

        {roles === null ? (
          <div className="flex justify-center py-20" data-testid="jobs-loading">
            <Loader2 className="h-8 w-8 animate-spin text-[#0d2f4d]" />
          </div>
        ) : roles.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#cfcbbc] bg-white/60 py-20 text-center" data-testid="jobs-empty">
            <p className="text-lg font-medium text-[#0d2f4d]">No open roles right now</p>
            <p className="mt-2 text-sm text-[#51637a]">
              New positions land often — join the talent pool and we'll match you as they come in.
            </p>
            <Link to="/signup/candidate" className="mt-6 inline-flex rounded-full bg-[#0d2f4d] px-6 py-3 text-sm font-semibold text-white hover:bg-[#1c4d78]">
              Join the talent pool
            </Link>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="jobs-grid">
            {roles.map((role) => (
              <JobCard key={role.id} role={role} />
            ))}
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
